import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  EditOutlined,
  GlobalOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  SkinOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import {
  contentSummary,
  formatDate,
  titleCase,
  valueOrDash,
  websiteOwnerTypeForWorkspace,
} from "./websiteUtils";
import "./OlreadiaFlowPages.css";
import DeleteModal from "components/websites/DeleteModal";

const { Paragraph, Text, Title } = Typography;

export default function WebsiteDetails() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, isOrganizationWorkspace } =
    useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const ownerType = websiteOwnerTypeForWorkspace({
    isOrganizationWorkspace,
    selectedWorkspace,
  });
  const isPortfolio = ownerType === "PORTFOLIO";
  const [details, setDetails] = useState(null);
  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) return;
    try {
      setLoading(true);
      setError("");
      const next = isPortfolio
        ? await olreadiaService.websites.detailsByUserPortfolio(
            selectedWorkspaceId,
            actorId,
          )
        : await olreadiaService.websites.detailsByWorkspace(
            selectedWorkspaceId,
            actorId,
          );
      setDetails(next);
      if (next?.website && !isPortfolio) {
        setDomains(
          await olreadiaService.domains
            .registrations(selectedWorkspaceId, actorId)
            .catch(() => null),
        );
      } else {
        setDomains(null);
      }
    } catch (nextError) {
      setError(nextError?.message || "Could not load website details.");
    } finally {
      setLoading(false);
    }
  }, [actorId, isPortfolio, selectedWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const website = details?.website;
  const summary = useMemo(() => contentSummary(details), [details]);
  const activeDomain =
    domains?.domains?.find((domain) => domain.status === "ACTIVE") ||
    domains?.domains?.[0];
  const previewUrl = activeDomain?.domain
    ? `https://${activeDomain.domain}`
    : details?.status?.previewUrl ||
      (website?.slug ? olreadiaService.websites.previewUrl(website.slug) : "");

  const handleDeleteConfirm = async (id) => {
    await olreadiaService.websites.deleteSite(id);
    setDeleteTarget(null);
    await load();
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon">
              <GlobalOutlined />
            </span>
            <div>
              <Text className="olreadia-flow-eyebrow">Website</Text>
              <Title level={2}>Website Details</Title>
              <Paragraph>
                Create or manage a{" "}
                {isPortfolio
                  ? "personal portfolio"
                  : "promotional organization"}{" "}
                website for {selectedWorkspace?.name || "this workspace"}.
              </Paragraph>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
              Refresh
            </Button>
          </section>

          {error ? (
            <Alert
              type="error"
              showIcon
              title="Unable to load website"
              description={error}
              style={{ marginBottom: 12 }}
            />
          ) : null}
          {loading && !details ? (
            <Card className="olreadia-flow-card">
              <Skeleton active paragraph={{ rows: 5 }} />
            </Card>
          ) : null}

          {!loading && !website ? (
            <Card className="olreadia-flow-card">
              <Empty description="No website yet">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    navigate(`${paths.websiteTemplates}?ownerType=${ownerType}`)
                  }
                >
                  Create Website
                </Button>
              </Empty>
            </Card>
          ) : null}

          {website ? (
            <Space orientation="vertical" size={12} style={{ width: "100%" }}>
              <Card className="olreadia-flow-card">
                <div className="olreadia-flow-card__top">
                  <span className="olreadia-flow-card__icon">
                    <GlobalOutlined />
                  </span>
                  <div>
                    <Title level={4}>
                      {valueOrDash(website.name || summary.headline)}
                    </Title>
                    <Text>
                      {olreadiaService.websites.previewUrl(website.slug)}
                    </Text>
                  </div>
                </div>
                <div className="olreadia-flow-actions">
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    href={olreadiaService.websites.previewUrl(website.slug)}
                    target="_blank"
                    disabled={!previewUrl}
                  >
                    Open Website
                  </Button>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() =>
                      navigate(
                        `${paths.websiteBuild(website.template)}?websiteId=${encodeURIComponent(website.id)}&mode=regenerate&ownerType=${ownerType}`,
                      )
                    }
                  >
                    Update Website Info
                  </Button>
                  <Button
                    icon={<SkinOutlined />}
                    onClick={() =>
                      navigate(
                        `${paths.websiteRegenerate}?websiteId=${encodeURIComponent(website.id)}&currentTemplate=${encodeURIComponent(website.template || "")}&mode=regenerate&ownerType=${ownerType}`,
                      )
                    }
                  >
                    Update Template
                  </Button>
                  {!isPortfolio && !domains?.hasRegisteredDomains ? (
                    <Button
                      icon={<GlobalOutlined />}
                      onClick={() =>
                        navigate(
                          `${paths.websiteDomain}?websiteId=${encodeURIComponent(website.id)}&suggestedName=${encodeURIComponent(website.slug || "")}`,
                        )
                      }
                    >
                      Custom Domain
                    </Button>
                  ) : null}
                </div>
              </Card>

              <Card className="olreadia-flow-card">
                <Title level={4}>Website Info</Title>
                <div className="olreadia-flow-detail-grid">
                  <div>
                    <Text>Tagline</Text>
                    <strong>
                      {valueOrDash(summary.tagline || website.tagline)}
                    </strong>
                  </div>
                  <div>
                    <Text>Template</Text>
                    <strong>{valueOrDash(website.template)}</strong>
                  </div>
                  <div>
                    <Text>Phone</Text>
                    <strong>{valueOrDash(summary.phone)}</strong>
                  </div>
                  <div>
                    <Text>Email</Text>
                    <strong>{valueOrDash(summary.email)}</strong>
                  </div>
                  <div>
                    <Text>City</Text>
                    <strong>{valueOrDash(summary.city)}</strong>
                  </div>
                  <div>
                    <Text>Created</Text>
                    <strong>{formatDate(website.createdAt)}</strong>
                  </div>
                  <div>
                    <Text>Updated</Text>
                    <strong>{formatDate(website.updatedAt)}</strong>
                  </div>
                  <div>
                    <Text>Published</Text>
                    <strong>{formatDate(website.publishedAt)}</strong>
                  </div>
                </div>
              </Card>

              {/* danger zone card */}
              <Card
                className="olreadia-flow-card"
                style={{
                  borderColor: "rgba(239,68,68,0.25)",
                  background: "#fef2f2",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#ef4444",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 14,
                  }}
                >
                  Danger zone
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "0.5px solid rgba(239,68,68,0.25)",
                    background: "#fff",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1a1a1a",
                        marginBottom: 3,
                      }}
                    >
                      Delete this website
                    </div>
                    <div style={{ fontSize: 12, color: "#6b6b6b" }}>
                      Permanently removes the website, all content, and
                      deployments. This cannot be undone.
                    </div>
                  </div>
                  <Button
                    danger
                    icon={
                      <i className="ti ti-trash" style={{ fontSize: 13 }} />
                    }
                    onClick={() =>
                      setDeleteTarget({
                        id: website.id,
                        name: website.name || website.slug || "this website",
                        slug: website.slug,
                      })
                    }
                    style={{ flexShrink: 0 }}
                  >
                    Delete website
                  </Button>
                </div>
              </Card>
            </Space>
          ) : null}
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>

      <DeleteModal
        open={!!deleteTarget}
        site={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
